/**
 * PROMOTION PRICES: show Discount Mixer promotions to Google and in Doofinder
 * ==========================================================================
 *
 * The problem
 *   Discount Mixer (the promotions app) does not change the Shopify prices. It only redraws the prices in the
 *   browser, and the real discount is applied at checkout. So everything that reads the Shopify price directly
 *   still sees the full price:
 *     - Google, through the product structured data (JSON-LD) → no sale price in the search results;
 *     - Doofinder, the search engine → no sale price in the search layer.
 *
 * What this file does
 *   1. On product pages: rewrites the product JSON-LD with the sale price plus the original price marked as
 *      "StrikethroughPrice" (the format Google asks for to show a sale).
 *   2. On every page: adds the sale price and a "-40 %" badge on the Doofinder result cards.
 *
 *   Both parts use the same function, getPromotion(), which copies Discount Mixer's own rules.
 *   Those rules were checked against Discount Mixer's storefront code (extension "discount-mixer-1381",
 *   files runtime-conditions.utils and runtime-base-discount-element).
 *
 * Debugging
 *   Every step is logged in the browser console with the prefix "[promo]".
 *   Logs are shown on the draft/dev themes only. On the live site, add ?promo_debug to the URL to see them.
 */
(() => {
  // ---------------------------------------------------------------------------------------------------------------
  // 0. Debug logs
  // ---------------------------------------------------------------------------------------------------------------

  // Shopify.theme.role is "main" on the published theme, and "unpublished" / "development" on previews.
  const isLiveTheme = window.Shopify?.theme?.role === 'main';
  const DEBUG = !isLiveTheme || location.search.includes('promo_debug');

  function log(name, value) {
    if (DEBUG) console.log(`[promo] ${name}`, value);
  }

  // ---------------------------------------------------------------------------------------------------------------
  // 1. Read the Discount Mixer configuration that the app prints in every page
  // ---------------------------------------------------------------------------------------------------------------

  const configScript = document.querySelector('script[data-dmixer-config="storefront"]');
  if (!configScript) {
    log('No Discount Mixer config on this page, nothing to do', null);
    return;
  }

  let dmixerConfig;
  try {
    dmixerConfig = JSON.parse(configScript.textContent);
  } catch (error) {
    log('Discount Mixer config is not valid JSON', error);
    return;
  }
  log('dmixerConfig', dmixerConfig);

  /*
   * The "product" campaigns, already sorted by priority: the FIRST campaign that matches wins.
   * Field names are shortened by Discount Mixer:
   *   d        discount value (40 = 40 % when t is "P")
   *   t        type: "P" = percentage, "F" = fixed amount off (€), "N" = new fixed price (€)
   *   m        maximum discount in € (optional)
   *   f        filters: the products included    (see matchesConditions)
   *   e        exclusions: the products excluded  (see matchesConditions)
   *   sd, ed   start date, end date (optional)
   *   ct       customer tags REQUIRED, e.g. ["Therapeute"] → campaign for professionals only
   *   ec       customer tags EXCLUDED, e.g. ["PN"]
   *   pr       priority (for information, the list is already sorted)
   *   c        collection handles (for display only, not used in the calculation)
   * "Volume" campaigns (price per quantity) are ignored: they start at 2 units and we show the price of 1 unit.
   */
  const campaigns = dmixerConfig.campaigns?.product ?? [];
  log('campaigns', campaigns);

  // ---------------------------------------------------------------------------------------------------------------
  // 2. Discount Mixer rules: "does this campaign apply to this variant, and at what price?"
  // ---------------------------------------------------------------------------------------------------------------

  function isCampaignRunning(campaign) {
    const now = Date.now();
    const start = campaign.sd ? Date.parse(campaign.sd) : null;
    const end = campaign.ed ? Date.parse(campaign.ed) : null;

    if (!start && !end) return true; // no dates: always on
    if (start && end) return now >= start && now <= end;
    if (start) return now >= start; // no end date: runs until it is stopped
    return false; // end date without start date: Discount Mixer ignores the campaign
  }

  function isCustomerAllowed(campaign, customerTags) {
    const requiredTags = campaign.ct ?? [];
    const excludedTags = campaign.ec ?? [];

    if (excludedTags.some((tag) => customerTags.includes(tag))) return false;
    if (requiredTags.length === 0) return true;
    return customerTags.some((tag) => requiredTags.includes(tag));
  }

  // listContainsAny("123, 456", [456, 789]) → true
  function listContainsAny(commaSeparatedValues, productValues) {
    const wantedValues = String(commaSeparatedValues)
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
    return [productValues].flat().some((value) => wantedValues.includes(String(value).trim()));
  }

  /*
   * Filters and exclusions use the same format. Each key is a list of comma-separated values ("" = not used):
   *   c = collection ids, i = product ids, vi = variant ids, v = vendors, p = product types, t = tags,
   *   l / h = lowest / highest price in €.
   * All the keys used must match (AND), unless "a" is true (then one match is enough: OR).
   */
  function matchesConditions(conditions, product, variant) {
    const results = [];
    if (conditions.c) results.push(listContainsAny(conditions.c, product.collectionIds));
    if (conditions.i) results.push(listContainsAny(conditions.i, product.id));
    if (conditions.vi) results.push(listContainsAny(conditions.vi, variant.id));
    if (conditions.v) results.push(listContainsAny(conditions.v, product.vendor));
    if (conditions.p) results.push(listContainsAny(conditions.p, product.type));
    if (conditions.t) results.push(listContainsAny(conditions.t, product.tags));
    if (conditions.l || conditions.h) {
      const priceInEuros = variant.price / 100;
      const isAboveMin = !conditions.l || priceInEuros >= Number(conditions.l);
      const isBelowMax = !conditions.h || priceInEuros <= Number(conditions.h);
      results.push(isAboveMin && isBelowMax);
    }
    return conditions.a ? results.some(Boolean) : results.every(Boolean);
  }

  // Returns the sale price in cents (Shopify prices are in cents: 4,30 € = 430).
  function calculateSalePrice(campaign, priceInCents) {
    const currencyRate = Number(window.Shopify?.currency?.rate) || 1;

    if (campaign.t === 'P') {
      let discount = (priceInCents * campaign.d) / 100;
      if (campaign.m && discount > campaign.m * 100) discount = campaign.m * 100;
      // Discount Mixer rounds the discount DOWN to the cent (40 % of 4,33 € = 1,732 € → 1,73 €).
      discount = Math.floor(discount + 1e-9);
      return priceInCents - discount;
    }
    if (campaign.t === 'F') return Math.max(0, Math.round(priceInCents - campaign.d * currencyRate * 100));
    if (campaign.t === 'N') return Math.round(campaign.d * currencyRate * 100);
    return null; // unknown type: show nothing rather than a wrong price
  }

  /**
   * Finds the promotion for one variant, exactly like Discount Mixer does.
   * @param product      { id, handle, vendor, type, tags, collectionIds, variants: [{ id, price }] }
   * @param variant      one of product.variants (price in cents)
   * @param customerTags tags of the logged-in customer ([] for a visitor who is not logged in, like Google)
   * @returns { campaign, regularPrice, salePrice, percentOff, startDate, endDate } or null
   */
  function getPromotion(product, variant, customerTags) {
    const campaignChecks = campaigns.map((campaign) => ({
      priority: campaign.pr,
      discount: `${campaign.d} ${campaign.t}`,
      isRunning: isCampaignRunning(campaign),
      isCustomerAllowed: isCustomerAllowed(campaign, customerTags),
      isProductIncluded: campaign.f ? matchesConditions(campaign.f, product, variant) : true,
      isProductExcluded: campaign.e ? matchesConditions(campaign.e, product, variant) : false,
      // A "new fixed price" campaign only counts if that price is lower than the current one.
      lowersThePrice: campaign.t !== 'N' || campaign.d * 100 < variant.price,
      campaign,
    }));
    log(`campaignChecks (${product.handle}, variant ${variant.id})`, campaignChecks);

    const winningCheck = campaignChecks.find(
      (check) =>
        check.isRunning &&
        check.isCustomerAllowed &&
        check.isProductIncluded &&
        !check.isProductExcluded &&
        check.lowersThePrice,
    );
    if (!winningCheck) {
      log(`promotion (${product.handle}, variant ${variant.id})`, null);
      return null;
    }

    const campaign = winningCheck.campaign;
    const salePrice = calculateSalePrice(campaign, variant.price);
    if (salePrice === null || salePrice >= variant.price) {
      log(`promotion (${product.handle}, variant ${variant.id}) - no lower price`, { campaign, salePrice });
      return null;
    }

    const promotion = {
      campaign,
      regularPrice: variant.price,
      salePrice,
      percentOff: Math.round(((variant.price - salePrice) / variant.price) * 100),
      // Full dates with time, e.g. "2026-09-30T22:00:00.000Z" (= 1 October, 00:00 in France). null if not set.
      startDate: campaign.sd ?? null,
      endDate: campaign.ed ?? null,
    };
    log(`promotion (${product.handle}, variant ${variant.id})`, promotion);
    return promotion;
  }

  function centsToSchemaPrice(cents) {
    return (cents / 100).toFixed(2); // 258 → "2.58"
  }

  // ---------------------------------------------------------------------------------------------------------------
  // 3. Product page: rewrite the JSON-LD read by Google
  // ---------------------------------------------------------------------------------------------------------------

  const SCHEMA_SELECTOR = 'script[type="application/ld+json"]';
  const STRIKETHROUGH_PRICE = 'https://schema.org/StrikethroughPrice';

  // Google is never logged in, so the schema always uses the price of a visitor without customer tags.
  const GOOGLE_CUSTOMER_TAGS = [];

  // Discount Mixer already gives the product data on product pages.
  const pageProduct = dmixerConfig.productContext?.id ? dmixerConfig.productContext : null;
  log('pageProduct', pageProduct);

  // Returns every { offer, owner } in the schema, where owner is the object that has the "offers" key.
  function findOffers(node, found = []) {
    if (Array.isArray(node)) {
      node.forEach((child) => findOffers(child, found));
    } else if (node && typeof node === 'object') {
      Object.entries(node).forEach(([key, value]) => {
        if (key === 'offers') {
          [value].flat().forEach((offer) => offer && found.push({ offer, owner: node }));
        } else {
          findOffers(value, found);
        }
      });
    }
    return found;
  }

  // The variant an offer is about: from "?variant=123" in its URL, or (for the global offer that the Skeepers
  // reviews app adds on the product group) the cheapest variant after discount.
  function findVariantForOffer(offer, owner, product) {
    const variantId = String(offer.url ?? offer['@id'] ?? '').match(/variant=(\d+)/)?.[1];
    if (variantId) return product.variants.find((variant) => String(variant.id) === variantId) ?? null;

    const ownerIsThisProduct = String(owner.url ?? '').includes(`/products/${product.handle}`);
    if (!ownerIsThisProduct) return null;

    const finalPrice = (variant) => getPromotion(product, variant, GOOGLE_CUSTOMER_TAGS)?.salePrice ?? variant.price;
    return [...product.variants].sort((a, b) => finalPrice(a) - finalPrice(b))[0] ?? null;
  }

  // Always sets the values again, even on an offer we already updated: the Skeepers reviews app rewrites
  // "price" after us with a wrong value ("17,70" becomes 17), on every product, in promotion or not.
  function addPromotionToOffer(offer, owner, product) {
    const variant = findVariantForOffer(offer, owner, product);
    if (!variant) return;

    const promotion = getPromotion(product, variant, GOOGLE_CUSTOMER_TAGS);
    if (!promotion) {
      offer.price = centsToSchemaPrice(variant.price); // no promotion: put back the real price (19 → "19.90")
      return;
    }

    offer.price = centsToSchemaPrice(promotion.salePrice);
    offer.priceSpecification = {
      '@type': 'UnitPriceSpecification',
      priceType: STRIKETHROUGH_PRICE,
      price: centsToSchemaPrice(promotion.regularPrice),
      priceCurrency: offer.priceCurrency,
    };
    // Sale duration: Google wants the start date too when there is an end date (Search Console warning
    // 'Champ "validFrom" manquant' otherwise).
    if (promotion.endDate) {
      offer.priceValidUntil = promotion.endDate;
      if (promotion.startDate) offer.validFrom = promotion.startDate;
    }
  }

  function updateProductSchema() {
    document.querySelectorAll(SCHEMA_SELECTOR).forEach((script) => {
      let schema;
      try {
        schema = JSON.parse(script.textContent);
      } catch {
        return; // a third-party script (reviews app...) that is still being written
      }

      const offers = findOffers(schema);
      if (offers.length === 0) return; // breadcrumb, FAQ...
      log('offersFound', offers);

      const schemaBefore = JSON.stringify(schema);
      offers.forEach(({ offer, owner }) => addPromotionToOffer(offer, owner, pageProduct));
      const schemaAfter = JSON.stringify(schema);

      // Only write when something changed, otherwise our own write would trigger the observer forever.
      if (schemaAfter !== schemaBefore) {
        script.textContent = schemaAfter;
        log('updatedSchema', schema);
      }
    });
  }

  // True when a DOM change adds or edits a JSON-LD script (the Skeepers reviews app rewrites it after load).
  function touchesSchema(mutation) {
    const isSchemaScript = (node) => node?.nodeType === Node.ELEMENT_NODE && node.matches(SCHEMA_SELECTOR);
    if (isSchemaScript(mutation.target) || isSchemaScript(mutation.target.parentNode)) return true;
    return [...mutation.addedNodes].some((node) => isSchemaScript(node) || node.querySelector?.(SCHEMA_SELECTOR));
  }

  // ---------------------------------------------------------------------------------------------------------------
  // 4. Doofinder: add the sale price on the search result cards
  // ---------------------------------------------------------------------------------------------------------------

  // On the site, a logged-in professional sees their own campaigns, so Doofinder uses the real customer tags.
  const customerTags = dmixerConfig.customer?.tags ?? [];
  log('customerTags', customerTags);

  const moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'fr', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || dmixerConfig.money?.currency || 'EUR',
  });

  // One request per product: handle → Promise of the product data (from templates/product.promo-data.liquid).
  const productDataRequests = new Map();

  function fetchProductData(handle) {
    if (!productDataRequests.has(handle)) {
      const url = `${window.Shopify?.routes?.root ?? '/'}products/${handle}?view=promo-data`;
      const request = fetch(url)
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);
      productDataRequests.set(handle, request);
    }
    return productDataRequests.get(handle);
  }

  async function addPromotionToDoofinderCard(card) {
    const pricing = card.querySelector('.dfd-card-pricing');
    const priceElement = pricing?.querySelector('.dfd-card-price');
    const alreadyOnSale = pricing?.querySelector('.dfd-card-price--sale');
    if (!priceElement || alreadyOnSale || card.dataset.promoStatus === 'loading') return;

    // "https://propos-noture.shop/products/acide-citrique" → "acide-citrique"
    const handle = String(card.getAttribute('dfd-value-link')).match(/\/products\/([^/?#]+)/)?.[1];
    if (!handle) return;

    card.dataset.promoStatus = 'loading';
    const product = await fetchProductData(handle);
    card.dataset.promoStatus = 'done';
    log(`doofinderProduct (${handle})`, product);
    if (!product?.variants?.length) return;

    // The card shows the price of one variant: find it by its price, or take the first one.
    const shownPrice = Math.round(Number(priceElement.dataset.value) * 100);
    const variant = product.variants.find((item) => item.price === shownPrice) ?? product.variants[0];
    log(`doofinderVariant (${handle})`, { shownPrice, variant });

    const promotion = getPromotion(product, variant, customerTags);
    // Check again: Doofinder may have redrawn the card while we were waiting for the product data.
    if (!promotion || !priceElement.isConnected || pricing.querySelector('.dfd-card-price--sale')) return;

    // Same HTML as Doofinder uses for a real sale price: its own CSS strikes through the old price.
    const saleElement = document.createElement('span');
    saleElement.className = 'dfd-card-price dfd-card-price--sale';
    saleElement.dataset.value = centsToSchemaPrice(promotion.salePrice);
    saleElement.textContent = moneyFormatter.format(promotion.salePrice / 100);
    priceElement.before(saleElement);

    // Doofinder's own discount badge, top right of the image.
    const flags = card.querySelector('.dfd-flags-container');
    if (flags && !flags.querySelector('[data-discount]')) {
      const badge = document.createElement('div');
      badge.className = 'dfd-card-flag';
      badge.dataset.discount = promotion.percentOff;
      badge.textContent = `-${promotion.percentOff} %`;
      flags.append(badge);
    }
    log(`doofinderCardUpdated (${handle})`, card);
  }

  // Doofinder redraws its cards often (new search, scroll...): update them at most once per frame.
  let isDoofinderUpdateScheduled = false;

  function scheduleDoofinderUpdate() {
    if (isDoofinderUpdateScheduled) return;
    isDoofinderUpdateScheduled = true;
    requestAnimationFrame(() => {
      isDoofinderUpdateScheduled = false;
      document.querySelectorAll('.dfd-card[dfd-value-link]').forEach(addPromotionToDoofinderCard);
    });
  }

  // ---------------------------------------------------------------------------------------------------------------
  // 5. Start
  // ---------------------------------------------------------------------------------------------------------------

  if (pageProduct) updateProductSchema();

  // Watch the page: other apps add or rewrite the JSON-LD and the Doofinder cards after the page has loaded.
  const observer = new MutationObserver((mutations) => {
    if (pageProduct && mutations.some(touchesSchema)) updateProductSchema();
    if (document.querySelector('.dfd-card')) scheduleDoofinderUpdate();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true, characterData: true });
})();
